import React, { useState, useEffect } from 'react';
import { FiSearch, FiFilter, FiRefreshCw, FiEdit, FiPlus, FiEye, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BaseUrl from '../../../Url/BaseUrl';
const ManageCityVillage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // State for search filters - only the three required parameters
  const [searchFilters, setSearchFilters] = useState({
    cityVillageCode: '',
    stateCode: '',
    districtCode: ''
  });

  // State for fetched data
  const [cityVillages, setCityVillages] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);

  // State for API status
  const [loading, setLoading] = useState({
    cityVillages: false,
    states: false,
    districts: false
  });
  const [error, setError] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });
  const [updatingStatus, setUpdatingStatus] = useState(null); // track which item is being updated


  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 3000);
  };

  // Helper: sort city/villages alphabetically by name (case‑insensitive)
  const sortCityVillagesByName = (items) => {
    return [...items].sort((a, b) => {
      const nameA = (a.cityVillageName || '').toLowerCase();
      const nameB = (b.cityVillageName || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
  };

  // Fetch master data on component mount
  useEffect(() => {
    fetchMasterData();
  }, []);

  const fetchMasterData = async () => {
    try {
      // Fetch states
      setLoading(prev => ({ ...prev, states: true }));
      const statesResponse = await fetch(`${BaseUrl.baseurl}States`, {
        method: 'GET',
        headers: { 'accept': 'text/plain' }
      });
      if (statesResponse.ok) {
        const statesData = await statesResponse.json();
        setStates(statesData);
      }
    } catch (err) {
      console.error('Error fetching states:', err);
      showNotification('error', t('failedToLoadStates', 'Failed to load states data'));
    } finally {
      setLoading(prev => ({ ...prev, states: false }));
    }

    try {
      // Fetch districts
      setLoading(prev => ({ ...prev, districts: true }));
      const districtsResponse = await fetch(`${BaseUrl.baseurl}Districts`, {
        method: 'GET',
        headers: { 'accept': 'text/plain' }
      });
      if (districtsResponse.ok) {
        const districtsData = await districtsResponse.json();
        setDistricts(districtsData);
      }
    } catch (err) {
      console.error('Error fetching districts:', err);
      showNotification('error', t('failedToLoadDistricts', 'Failed to load districts data'));
    } finally {
      setLoading(prev => ({ ...prev, districts: false }));
    }
  };

  // Handle search input changes
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Search function using POST with JSON body
  const handleSearch = async () => {
    setError('');
    setHasSearched(false);
    setLoading(prev => ({ ...prev, cityVillages: true }));

    try {
      const requestBody = {};
      if (searchFilters.cityVillageCode.trim()) {
        requestBody.cityVillageCode = searchFilters.cityVillageCode.trim();
      }
      if (searchFilters.stateCode.trim()) {
        requestBody.stateCode = searchFilters.stateCode.trim();
      }
      if (searchFilters.districtCode.trim()) {
        requestBody.districtCode = searchFilters.districtCode.trim();
      }

      if (Object.keys(requestBody).length === 0) {
        setError(t('enterSearchCriteria', 'Please enter at least one search criteria'));
        setLoading(prev => ({ ...prev, cityVillages: false }));
        return;
      }

      const url = `${BaseUrl.baseurl}CityVillages/CityVillageSearch`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`${t('httpError', 'HTTP error! Status:')} ${response.status}`);
      }

      const data = await response.json();
      // Sort results alphabetically by city/village name
      const sortedData = sortCityVillagesByName(data);
      setCityVillages(sortedData);
      setHasSearched(true);

      if (sortedData.length === 0) {
        showNotification('info', t('noCityVillagesFound', 'No city/villages found matching your search criteria'));
      }

    } catch (err) {
      console.error('Search error:', err);
      setError(t('failedToFetchData', 'Failed to fetch data. Please check your search criteria and try again.'));
      setCityVillages([]);
      showNotification('error', t('searchFailed', 'Failed to search city/villages'));
    } finally {
      setLoading(prev => ({ ...prev, cityVillages: false }));
    }
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchFilters({
      cityVillageCode: '',
      stateCode: '',
      districtCode: ''
    });
    setCityVillages([]);
    setError('');
    setHasSearched(false);
  };

  // Handle enter key press for search
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Handle edit action
  const handleEdit = (cityVillage) => {
    navigate(`/route-finder/city-village/edit/${cityVillage.cityVillageCode}`, {
      state: { cityVillage }
    });
  };

  // Unified toggle status (activate/deactivate)
  const toggleStatus = async (cityVillage) => {
    setUpdatingStatus(cityVillage.cityVillageCode);
    const updatedStatus = !cityVillage.status;
    const updatedCityVillage = {
      ...cityVillage,
      status: updatedStatus
    };

    try {
      const response = await fetch(`${BaseUrl.baseurl}CityVillages/${cityVillage.cityVillageCode}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'accept': 'text/plain'
        },
        body: JSON.stringify(updatedCityVillage)
      });
      console.log(response);
      if (response.ok) {
        setCityVillages(prev => {
          const updatedList = prev.map(item =>
            item.cityVillageCode === cityVillage.cityVillageCode
              ? { ...item, status: updatedStatus }
              : item
          );
          // Maintain alphabetical order after status change
          return sortCityVillagesByName(updatedList);
        });
        showNotification('success', t('statusUpdated', 'City/Village {{status}} successfully!', {
          status: updatedStatus ? t('activated') : t('deactivated')
        }));
      } else {
        const errorText = await response.text();
        throw new Error(`${t('httpError', 'HTTP error! status:')} ${response.status}, ${t('details', 'details:')} ${errorText}`);
      }
    } catch (error) {
      console.error('Error updating city/village status:', error);
      showNotification('error', t('updateStatusError', 'Error updating city/village status. Please try again.'));
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Helper to get district name from district code
  const getDistrictName = (districtCode) => {
    if (!districtCode) return t('na', 'N/A');
    const district = districts.find(d => d.districtCode === districtCode);
    return district ? district.districtName : t('na', 'N/A');
  };

  // Handle view details (optional, kept but commented out in UI)
  const handleViewDetails = (cityVillage) => {
    navigate(`/route-finder/city-village/view/${cityVillage.cityVillageCode}`, {
      state: { cityVillage }
    });
  };

  return (
    <div className="manage-container">
      {/* Page Header */}
      <div>
        <button className="btn-back" onClick={() => navigate(-1)}>
          ← Back
        </button>
      </div>

      {notification.message && (
        <div className={`alert alert-${notification.type}`}>
          <span>{notification.message}</span>
          <button onClick={() => setNotification({ type: '', message: '' })}>×</button>
        </div>
      )}

      {/* Search & Filter Section */}
      <div className="search-filter-card">
        <div className="filter-header">
          <FiFilter className="filter-icon" />
          <h3>{t('searchCriteria', 'Search Criteria')}</h3>
        </div>

        <div className="filter-grid">
          {/* City/Village Code */}
          <div className="input-group">
            <label>{t('cityVillageCode', 'City/Village Code')}</label>
            <input
              type="text"
              name="cityVillageCode"
              value={searchFilters.cityVillageCode}
              onChange={handleFilterChange}
              onKeyPress={handleKeyPress}
              placeholder={t('enterCodePlaceholder', 'Enter code (e.g., 613063)')}
              disabled={loading.cityVillages}
              className="search-input"
            />
            <small className="input-hint">{t('exactMatchRequired', 'Exact match required')}</small>
          </div>

          {/* State Selection */}
          <div className="input-group">
            <label>{t('state', 'State')}</label>
            <select
              name="stateCode"
              value={searchFilters.stateCode}
              onChange={handleFilterChange}
              disabled={loading.states || loading.cityVillages}
              className="search-select"
            >
              <option value="">{t('selectStateOptional', 'Select State (Optional)')}</option>
              {states.map(state => (
                <option key={state.stateCode} value={state.stateCode}>
                  {state.stateCode} - {state.stateName || t('unknown', 'Unknown')}
                </option>
              ))}
            </select>
            {loading.states && <small className="input-hint">{t('loadingStates', 'Loading states...')}</small>}
          </div>

          {/* District Selection */}
          <div className="input-group">
            <label>{t('district', 'District')}</label>
            <select
              name="districtCode"
              value={searchFilters.districtCode}
              onChange={handleFilterChange}
              disabled={loading.districts || loading.cityVillages}
              className="search-select"
            >
              <option value="">{t('selectDistrictOptional', 'Select District (Optional)')}</option>
              {districts.map(district => (
                <option key={district.districtCode} value={district.districtCode}>
                  {district.districtCode} - {district.districtName || t('unknown', 'Unknown')}
                </option>
              ))}
            </select>
            {loading.districts && <small className="input-hint">{t('loadingDistricts', 'Loading districts...')}</small>}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="filter-actions">
          <button
            className="btn-secondary"
            onClick={handleClearFilters}
            disabled={loading.cityVillages}
          >
            <FiRefreshCw /> {t('clear', 'Clear')}
          </button>

          <button
            className="btn-primary"
            onClick={handleSearch}
            disabled={loading.cityVillages}
          >
            {loading.cityVillages ? (
              <>
                <ClipLoader size={16} color="#fff" /> {t('searching', 'Searching...')}
              </>
            ) : (
              <>
                <FiSearch /> {t('search', 'Search')}
              </>
            )}
          </button>
        </div>

        {/* Search Instructions */}
        <div className="search-instructions">
          <p><strong>{t('note', 'Note:')}</strong> {t('searchInstructions', 'Enter at least one of the three criteria to search. You can use any combination of Code, State, and District.')}</p>
        </div>
      </div>

      {/* Results Section */}
      <div className="results-section">
        {loading.cityVillages ? (
          <div className="loading-state">
            <ClipLoader size={40} color="#00BCD4" />
            <p>{t('searchingData', 'Searching city/village data...')}</p>
          </div>
        ) : hasSearched ? (
          <>
            <div className="results-header">
              <h3>
                {t('searchResults', 'Search Results')}
                <span className="results-count">({cityVillages.length} {t('recordsFound', 'records found')})</span>
              </h3>
            </div>

            {cityVillages.length > 0 ? (
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>{t('cityVillageCode', 'City/Village Code')}</th>
                      <th>{t('cityVillageName', 'City/Village Name')}</th>
                      <th>{t('districtCode', 'District Code')}</th>
                      <th>{t('districtName', 'District Name')}</th> {/* New Column */}
                      <th>{t('status', 'Status')}</th>
                      <th>{t('actions', 'Actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cityVillages.map((item, index) => (
                      <tr key={`${item.cityVillageCode}-${index}`}>
                        <td>
                          <span className="code-badge">
                            {item.cityVillageCode}
                          </span>
                        </td>
                        <td className="name-cell">{item.cityVillageName || t('na', 'N/A')}</td>
                        <td>{item.districtCode || t('na', 'N/A')}</td>
                        <td>{getDistrictName(item.districtCode)}</td>  {/* District Name */}
                        <td>
                          <button
                            className={`status-toggle ${item.status ? 'active' : 'inactive'}`}
                            onClick={() => toggleStatus(item)}
                            disabled={updatingStatus === item.cityVillageCode}
                            title={item.status ? t('clickToDeactivate', 'Click to deactivate') : t('clickToActivate', 'Click to activate')}
                          >
                            {updatingStatus === item.cityVillageCode ? (
                              <ClipLoader size={12} color={item.status ? "#155724" : "#721c24"} />
                            ) : (
                              item.status ? (
                                <>
                                  <FiCheckCircle /> {t('active', 'Active')}
                                </>
                              ) : (
                                <>
                                  <FiXCircle /> {t('inactive', 'Inactive')}
                                </>
                              )
                            )}
                          </button>
                        </td>
                        <td className="actions-cell">
                          {/* Edit button */}
                          <button
                            className="btn-icon btn-edit"
                            onClick={() => handleEdit(item)}
                            title={t('edit', 'Edit')}
                          >
                            <FiEdit />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty-state">
                <FiSearch size="3em" />
                <p>{t('noRecordsFound', 'No records found matching your search criteria. Try different filters.')}</p>
              </div>
            )}
          </>
        ) : (
          <div className="initial-state">
            <div className="initial-message">
              <FiSearch className="initial-icon" />
              <h4>{t('readyToSearch', 'Ready to Search')}</h4>
              <p>{t('enterCriteriaMessage', 'Enter at least one search criteria above and click "Search" to find city/village records.')}</p>
              <p className="hint-text">{t('searchCombinationHint', 'You can search by Code, State, District, or any combination.')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Styles */}
      <style jsx>{`
        /* existing styles – unchanged */
        .manage-container {
          padding: 2rem;
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
        }

        .alert {
          padding: 1rem 1.5rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          display: flex;
          justify-content: space-between;
          align-items: center;
          animation: slideIn 0.3s ease-out;
        }

        .alert-success {
          background: #d4edda;
          color: #155724;
          border: 1px solid #c3e6cb;
        }

        .alert-error {
          background: #f8d7da;
          color: #721c24;
          border: 1px solid #f5c6cb;
        }

        .alert-info {
          background: #d1ecf1;
          color: #0c5460;
          border: 1px solid #bee5eb;
        }

        .search-filter-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .filter-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 1.5rem;
          color: #333;
        }

        .filter-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 1rem;
          margin-bottom: 1.5rem;
        }

        .input-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .input-group label {
          font-weight: 600;
          font-size: 0.9rem;
          color: #495057;
        }

        .search-input, .search-select {
          padding: 0.75rem;
          border: 1px solid #dee2e6;
          border-radius: 6px;
          font-size: 0.95rem;
          transition: border-color 0.2s;
          width: 100%;
          box-sizing: border-box;
        }

        .search-input:focus, .search-select:focus {
          outline: none;
          border-color: #00BCD4;
          box-shadow: 0 0 0 3px rgba(0, 188, 212, 0.1);
        }

        .input-hint {
          font-size: 0.75rem;
          color: #6c757d;
          font-style: italic;
        }

        .error-message {
          background: #f8d7da;
          color: #721c24;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          margin: 1rem 0;
          font-size: 0.9rem;
        }

        .filter-actions {
          display: flex;
          justify-content: flex-end;
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #eee;
        }

        .btn-primary {
          background: linear-gradient(135deg, #FFFDE7 0%, #FFF9C4 100%);
          color: black;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          transition: all 0.2s;
        }

        .btn-primary:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
        }

        .btn-secondary {
          background: #f8f9fa;
          color: #495057;
          border: 1px solid #dee2e6;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
        }

        .search-instructions {
          background: #e3f2fd;
          color: #1565c0;
          padding: 0.75rem 1rem;
          border-radius: 6px;
          margin-top: 1rem;
          font-size: 0.9rem;
        }

        .results-section {
          background: white;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          padding: 1.5rem;
        }

        .results-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
        }

        .results-count {
          font-size: 0.9rem;
          color: #666;
          margin-left: 0.5rem;
          font-weight: normal;
        }

        .loading-state, .empty-state, .initial-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem;
          text-align: center;
        }

        .initial-icon {
          font-size: 3em;
          color: #00BCD4;
          margin-bottom: 1rem;
        }

        .table-container {
          overflow-x: auto;
        }

        .data-table {
          width: 100%;
          border-collapse: collapse;
        }

        .data-table th {
          background: #f8f9fa;
          padding: 1rem;
          text-align: left;
          font-weight: 600;
          color: #495057;
          border-bottom: 2px solid #dee2e6;
        }

        .data-table td {
          padding: 1rem;
          border-bottom: 1px solid #eee;
        }

        .code-badge {
          background: #e3f2fd;
          color: #1976d2;
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-toggle {
          border: none;
          padding: 0.4rem 0.8rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 0.3rem;
          transition: all 0.2s;
        }

        .status-toggle.active {
          background: #d4edda;
          color: #155724;
        }

        .status-toggle.inactive {
          background: #f8d7da;
          color: #721c24;
        }

        .status-toggle:hover {
          transform: scale(1.02);
        }

        .actions-cell {
          min-width: 80px;
        }

        .btn-icon {
          width: 36px;
          height: 36px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #fff3e0;
          color: #f57c00;
          transition: all 0.2s;
        }

        .btn-icon:hover {
          transform: translateY(-2px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        @keyframes slideIn {
          from {
            transform: translateY(-20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default ManageCityVillage;